import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { QueuedEvent } from '../types';

/**
 * RavePal analytics: in-app actions only, no cookies or PII.
 * Offline: events are stored in IndexedDB (idb). On "online", they are sent via POST to
 * VITE_CLOUDFLARE_ANALYTICS_WORKER_URL. Use useAnalytics() or useTrackEvent() in components.
 */
const DB_NAME = 'ravepal-analytics';
const DB_VERSION = 1;
const STORE_NAME = 'analytics_queue';

const BEACON_SCRIPT_URL = 'https://static.cloudflareinsights.com/beacon.min.js';

interface AnalyticsDB extends DBSchema {
  [STORE_NAME]: {
    key: string;
    value: QueuedEvent;
  };
}

let dbPromise: Promise<IDBPDatabase<AnalyticsDB>> | null = null;

async function getDB(): Promise<IDBPDatabase<AnalyticsDB>> {
  if (!dbPromise) {
    dbPromise = openDB<AnalyticsDB>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        database.createObjectStore(STORE_NAME, { keyPath: 'id' });
      },
    });
  }
  return dbPromise;
}

function generateId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getToken(): string | undefined {
  return import.meta.env.VITE_CLOUDFLARE_BEACON_TOKEN as string | undefined;
}

function getWorkerUrl(): string | undefined {
  return import.meta.env.VITE_CLOUDFLARE_ANALYTICS_WORKER_URL as string | "https://ravepal-analytics-proxy.valphusky.workers.dev/";
}

function isMockEnabled(): boolean {
  return import.meta.env.VITE_USE_ANALYTICS_MOCK === 'true';
}

function isTryCloudflareOrigin(): boolean {
  return typeof window !== 'undefined' && window.location.origin.includes('trycloudflare.com');
}

/**
 * Inject the Cloudflare beacon script when online.
 * Skips when origin is trycloudflare.com (CORS blocks cloudflareinsights.com from that origin).
 * Uses requestIdleCallback or setTimeout to avoid blocking main thread / Map render.
 */
function injectBeaconScript(): void {
  const token = getToken();
  if (!token || typeof document === 'undefined') return;
  if (isTryCloudflareOrigin()) return;

  const schedule = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 0));
  schedule(() => {
    if (document.querySelector('script[src*="cloudflareinsights.com"]')) return;
    const script = document.createElement('script');
    script.defer = true;
    script.src = BEACON_SCRIPT_URL;
    script.setAttribute('data-cf-beacon', JSON.stringify({ token, spa: true }));
    document.head.appendChild(script);
  });
}

/**
 * Queue an event to IndexedDB (offline path).
 */
async function queueEvent(event: Omit<QueuedEvent, 'id'>): Promise<void> {
  const db = await getDB();
  const record: QueuedEvent = {
    ...event,
    id: generateId(),
  };
  const tx = db.transaction(STORE_NAME, 'readwrite');
  await tx.objectStore(STORE_NAME).add(record);
  await tx.done;
}

/**
 * Get all queued events from IndexedDB.
 */
async function getQueuedEvents(): Promise<QueuedEvent[]> {
  const db = await getDB();
  const tx = db.transaction(STORE_NAME, 'readonly');
  const events = await tx.objectStore(STORE_NAME).getAll();
  await tx.done;
  return events;
}

/**
 * Remove events from the queue by IDs.
 */
async function removeQueuedEvents(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const db = await getDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  const store = tx.objectStore(STORE_NAME);
  await Promise.all(ids.map((id) => store.delete(id)));
  await tx.done;
}

/**
 * Sync queued analytics events to the Worker when back online.
 * Triggered by the browser's 'online' event.
 * In mock mode: logs events to console and simulates successful sync.
 */
export async function syncAnalytics(): Promise<void> {
  if (!navigator.onLine) return;

  if (isMockEnabled()) {
    try {
      const events = await getQueuedEvents();
      if (events.length === 0) return;
      console.log('[Cloudflare Analytics Mock] Sync: flushing', events.length, 'queued events', events);
      await removeQueuedEvents(events.map((e) => e.id));
      console.log('[Cloudflare Analytics Mock] Sync: complete');
    } catch {
      // Ignore
    }
    return;
  }

  const workerUrl = getWorkerUrl();
  if (!workerUrl) return;

  try {
    const events = await getQueuedEvents();
    if (events.length === 0) return;

    const response = await fetch(workerUrl, {
      method: 'POST',
      body: JSON.stringify({ events }),
      headers: { 'Content-Type': 'application/json' },
    });

    if (response.ok) {
      await removeQueuedEvents(events.map((e) => e.id));
    }
  } catch {
    // Sync will retry on next online event
  }
}

export class CloudflareAnalytics {
  private beaconInjected = false;

  /**
   * Initialize analytics: inject beacon when online, register online listener.
   * In mock mode: skips beacon injection, registers online listener for simulated sync.
   */
  init(): void {
    if (!isMockEnabled() && !getToken()) return;

    const schedule = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 0));
    schedule(() => {
      if (isMockEnabled()) {
        console.log('[Cloudflare Analytics Mock] Initialized; events will log to console');
      } else if (navigator.onLine && !this.beaconInjected) {
        injectBeaconScript();
        this.beaconInjected = true;
      }
      window.addEventListener('online', this.handleOnline);
    });
  }

  private handleOnline = (): void => {
    if (!isMockEnabled() && !this.beaconInjected) {
      injectBeaconScript();
      this.beaconInjected = true;
    }
    syncAnalytics();
  };

  /**
   * Track an event. Online: send to Worker; offline: queue to IndexedDB.
   * Fire-and-forget; does not block the main thread.
   * In mock mode: logs to console; offline events are queued and synced on reconnect.
   */
  trackEvent(name: string, data?: Record<string, unknown>): void {
    const event: Omit<QueuedEvent, 'id'> = {
      timestamp: Date.now(),
      pagePath: typeof window !== 'undefined' ? window.location.pathname : '/',
      action: name,
      data,
    };

    if (isMockEnabled()) {
      if (navigator.onLine) {
        console.log('[Cloudflare Analytics Mock] Event:', name, event);
      } else {
        const record: QueuedEvent = { ...event, id: generateId() };
        console.log('[Cloudflare Analytics Mock] Offline: queued', name, record);
        queueEvent(event).catch(() => {});
      }
      return;
    }

    const token = getToken();
    if (!token) return;

    if (navigator.onLine) {
      const workerUrl = getWorkerUrl();
      if (workerUrl) {
        const payload: QueuedEvent = { ...event, id: generateId() };
        const blob = new Blob([JSON.stringify({ events: [payload] })], {
          type: 'application/json',
        });
        if (navigator.sendBeacon?.(workerUrl, blob)) return;
        fetch(workerUrl, {
          method: 'POST',
          body: JSON.stringify({ events: [payload] }),
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        }).catch(() => {});
      }
    } else {
      queueEvent(event).catch(() => {});
    }
  }
}

let instance: CloudflareAnalytics | null = null;

/**
 * Get the singleton CloudflareAnalytics instance.
 */
export function getCloudflareAnalytics(): CloudflareAnalytics {
  if (!instance) {
    instance = new CloudflareAnalytics();
  }
  return instance;
}
